<template>
  <div class="plant-equipment-tab timesheet-tab-shell">
    <div class="plant-toolbar timesheet-tab-toolbar">
      <div class="timesheet-toolbar-top">
        <div class="plant-toolbar-heading">
          <h2 class="timesheet-tab-title">Plant & Equipment</h2>
          <p class="timesheet-tab-subtitle">
            Manage equipment tradies can log against timesheets
          </p>
        </div>
        <div class="timesheet-toolbar-actions">
          <b-form-checkbox
            v-model="showArchived"
            switch
            class="plant-archive-switch"
          >
            Show archived
          </b-form-checkbox>
          <b-button
            v-if="canManage"
            variant="outline-secondary"
            size="sm"
            @click="downloadCsvTemplate"
          >
            Download Template
          </b-button>
          <b-button
            v-if="canManage"
            variant="outline-primary"
            size="sm"
            @click="csvImportModalOpen = true"
          >
            Import CSV
          </b-button>
          <b-button
            v-if="canManage"
            variant="primary"
            size="sm"
            @click="openCreateModal"
          >
            + Add Equipment
          </b-button>
        </div>
      </div>
    </div>

    <div class="plant-subnav">
      <button
        type="button"
        class="plant-subnav-item"
        :class="{ active: activeView === 'catalogue' }"
        @click="activeView = 'catalogue'"
      >
        Assets
      </button>
      <button
        type="button"
        class="plant-subnav-item"
        :class="{ active: activeView === 'report' }"
        @click="activeView = 'report'"
      >
        Claim Report
      </button>
    </div>

    <template v-if="activeView === 'catalogue'">
      <div class="plant-content timesheet-tab-content">
        <div class="plant-catalogue-filters">
          <b-form-group
            label="Project"
            class="plant-catalogue-filter-field mb-0"
          >
            <b-form-select
              v-model="catalogueFilters.projectId"
              :options="projectOptions"
              size="sm"
              @change="onCatalogueFilterChange"
            />
          </b-form-group>
          <b-form-group
            label="Search"
            class="plant-catalogue-filter-field mb-0"
          >
            <b-form-input
              v-model="catalogueFilters.search"
              debounce="300"
              size="sm"
              placeholder="Search name or plant ID / rego"
              @update="onCatalogueFilterChange"
            />
          </b-form-group>
        </div>

        <div v-if="canManage && selectedIds.length" class="plant-bulk-bar">
          <span class="plant-bulk-count"
            >{{ selectedIds.length }} selected</span
          >
          <b-button
            variant="outline-danger"
            size="sm"
            :disabled="bulkDeleting"
            @click="onBulkDeleteClick"
          >
            <b-spinner v-if="bulkDeleting" small class="mr-1" />
            Delete selected
          </b-button>
          <b-button variant="link" size="sm" @click="selectedIds = []">
            Clear
          </b-button>
        </div>

        <b-overlay :show="loading" rounded="sm">
          <div class="plant-table-wrap">
            <table class="table plant-table">
              <thead>
                <tr>
                  <th v-if="canManage" class="plant-select-col">
                    <b-form-checkbox
                      :checked="allSelected"
                      :indeterminate="someSelected"
                      :disabled="items.length === 0"
                      @change="toggleSelectAll"
                    />
                  </th>
                  <th>Name</th>
                  <th>Plant ID / Rego</th>
                  <th>Rate</th>
                  <th>Project scope</th>
                  <th>Status</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="items.length === 0">
                  <td
                    :colspan="canManage ? 7 : 6"
                    class="text-center text-muted py-4"
                  >
                    {{
                      showArchived
                        ? 'No equipment found.'
                        : 'No equipment added yet.'
                    }}
                  </td>
                </tr>
                <tr v-for="equip in items" :key="equip.id">
                  <td v-if="canManage" class="plant-select-col">
                    <b-form-checkbox
                      :checked="selectedIds.includes(equip.id)"
                      @change="toggleSelected(equip.id, $event)"
                    />
                  </td>
                  <td>{{ equip.name }}</td>
                  <td>{{ equip.plant_id_rego || '-' }}</td>
                  <td>
                    ${{ formatRate(equip.default_rate) }} /
                    {{ equip.rate_type === 'day' ? 'day' : 'hr' }}
                  </td>
                  <td>
                    <span
                      v-if="equip.applies_to_all_projects"
                      class="scope-badge scope-badge-all"
                    >
                      All projects
                    </span>
                    <span
                      v-else
                      :id="`plant-scope-${equip.id}`"
                      class="scope-badge scope-badge-some"
                    >
                      {{ (equip.projects || []).length }}
                      {{
                        (equip.projects || []).length === 1
                          ? 'project'
                          : 'projects'
                      }}
                    </span>
                    <b-popover
                      v-if="!equip.applies_to_all_projects"
                      :target="`plant-scope-${equip.id}`"
                      triggers="hover focus"
                      placement="top"
                    >
                      <div
                        v-for="p in equip.projects || []"
                        :key="p.id"
                        class="scope-popover-row"
                      >
                        {{ p.name }}
                      </div>
                    </b-popover>
                  </td>
                  <td>
                    <span
                      class="status-pill status-pill-sm"
                      :class="
                        equip.is_archived
                          ? 'status-rejected'
                          : 'status-approved'
                      "
                    >
                      {{ equip.is_archived ? 'Archived' : 'Active' }}
                    </span>
                  </td>
                  <td class="text-right">
                    <b-button
                      v-if="canManage"
                      variant="link"
                      size="sm"
                      class="p-0 mr-3"
                      @click="openEditModal(equip)"
                    >
                      Edit
                    </b-button>
                    <b-button
                      v-if="canManage && !equip.is_archived"
                      variant="link"
                      size="sm"
                      class="p-0 mr-3 text-danger"
                      @click="onArchiveClick(equip)"
                    >
                      Archive
                    </b-button>
                    <b-button
                      v-if="canManage && equip.is_archived"
                      variant="link"
                      size="sm"
                      class="p-0 mr-3"
                      @click="onUnarchiveClick(equip)"
                    >
                      Unarchive
                    </b-button>
                    <b-button
                      v-if="canManage"
                      variant="link"
                      size="sm"
                      class="p-0 text-danger"
                      @click="onDeleteClick(equip)"
                    >
                      Delete
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-overlay>

        <div v-if="items.length" class="plant-pagination-bar">
          <div class="plant-pagination-size">
            <span>Show</span>
            <b-form-select
              v-model="pagination.perPage"
              :options="[10, 50, 100]"
              size="sm"
              @change="onPerPageChange"
            />
          </div>
          <div v-if="meta" class="plant-pagination-nav">
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="pagination.page <= 1"
              @click="goToPage(pagination.page - 1)"
            >
              Prev
            </b-button>
            <span class="plant-pagination-label">
              Page {{ meta.current_page }} of {{ meta.last_page }} ({{
                meta.total
              }}
              total)
            </span>
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="pagination.page >= meta.last_page"
              @click="goToPage(pagination.page + 1)"
            >
              Next
            </b-button>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="activeView === 'report'">
      <div class="plant-content timesheet-tab-content">
        <div class="cost-report-filters">
          <b-form-group
            label="Project"
            class="cost-report-field cost-report-project mb-0"
          >
            <b-form-select
              v-model="reportFilters.projectId"
              :options="projectOptions"
              class="cost-report-control"
            />
          </b-form-group>
          <b-form-group
            label="From"
            class="cost-report-field cost-report-date mb-0"
          >
            <b-form-datepicker
              v-model="reportFilters.dateFrom"
              locale="en-AU"
              :date-format-options="{
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              }"
              today-button
              reset-button
              close-button
              show-decade-nav
              class="cost-report-datepicker"
            />
          </b-form-group>
          <b-form-group
            label="To"
            class="cost-report-field cost-report-date mb-0"
          >
            <b-form-datepicker
              v-model="reportFilters.dateTo"
              :min="reportFilters.dateFrom || undefined"
              locale="en-AU"
              :date-format-options="{
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              }"
              today-button
              reset-button
              close-button
              show-decade-nav
              class="cost-report-datepicker"
            />
          </b-form-group>
          <b-button
            variant="primary"
            size="sm"
            class="cost-report-action"
            :disabled="!canRunReport || reportLoading"
            @click="runCostReport"
          >
            Run report
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="cost-report-action"
            :disabled="!canRunReport || reportLoading || !reportRows.length"
            @click="exportCostReport"
          >
            Export CSV
          </b-button>
        </div>

        <b-overlay :show="reportLoading" rounded="sm">
          <div class="plant-table-wrap">
            <table class="table plant-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Plant/Equipment</th>
                  <th>Plant ID / Rego</th>
                  <th>Type</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Rate</th>
                  <th class="text-right">Claim Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!reportRan">
                  <td colspan="9" class="text-center text-muted py-4">
                    Choose a date range and run the report.
                  </td>
                </tr>
                <tr v-else-if="reportRows.length === 0">
                  <td colspan="9" class="text-center text-muted py-4">
                    No plant/equipment usage in this range.
                  </td>
                </tr>
                <tr v-for="(r, i) in reportRows" :key="i">
                  <td>{{ r.project_name }}</td>
                  <td>{{ formatReportDate(r.usage_day) }}</td>
                  <td>{{ formatReportDay(r.usage_day) }}</td>
                  <td>{{ r.plant_name }}</td>
                  <td>{{ r.plant_id_rego || '-' }}</td>
                  <td>{{ r.rate_type_snapshot }}</td>
                  <td class="text-right">{{ r.quantity }}</td>
                  <td class="text-right">${{ formatRate(r.rate_snapshot) }}</td>
                  <td class="text-right">${{ formatRate(r.claim_value) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="reportRan && reportRows.length">
                <tr>
                  <td colspan="8" class="text-right font-weight-bold">
                    Grand Total
                  </td>
                  <td class="text-right font-weight-bold">
                    ${{ formatRate(reportGrandTotal) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </b-overlay>
      </div>
    </template>

    <plant-equipment-modal
      :show="modalOpen"
      :item="modalItem"
      @update:show="modalOpen = $event"
      @saved="onModalSaved"
    />

    <plant-equipment-csv-import-modal
      :show="csvImportModalOpen"
      @update:show="csvImportModalOpen = $event"
      @imported="onCsvImported"
    />

    <plant-equipment-csv-result-modal
      v-model="csvResultOpen"
      :success-count="csvResult.success_count"
      :failure-count="csvResult.failure_count"
      :errors="csvResult.errors"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters, mapActions } from 'vuex'
import PlantEquipmentModal from '@/components/timesheets/PlantEquipmentModal.vue'
import PlantEquipmentCsvImportModal from '@/components/timesheets/PlantEquipmentCsvImportModal.vue'
import PlantEquipmentCsvResultModal from '@/components/modals/PlantEquipmentCsvResultModal.vue'

export default {
  name: 'PlantEquipmentTab',

  components: {
    PlantEquipmentModal,
    PlantEquipmentCsvImportModal,
    PlantEquipmentCsvResultModal,
  },

  data() {
    return {
      loading: false,
      showArchived: false,
      modalOpen: false,
      modalItem: null,
      csvImportModalOpen: false,
      csvResultOpen: false,
      csvResult: { success_count: 0, failure_count: 0, errors: [] },
      selectedIds: [],
      bulkDeleting: false,
      catalogueFilters: { projectId: null, search: '' },
      pagination: { page: 1, perPage: 10 },
      activeView: 'catalogue',
      reportFilters: { projectId: null, dateFrom: '', dateTo: '' },
      reportLoading: false,
      reportRan: false,
      reportRows: [],
      reportGrandTotal: 0,
    }
  },

  computed: {
    ...mapGetters('plantEquipment', ['items', 'meta']),
    ...mapGetters('auth', ['hasPermission']),
    ...mapGetters('project', ['projects']),

    canManage() {
      return this.hasPermission('timesheets.plant.manage')
    },

    projectOptions() {
      return [{ value: null, text: 'All Projects' }].concat(
        (this.projects || []).map((p) => ({ value: p.id, text: p.name })),
      )
    },

    canRunReport() {
      return !!(this.reportFilters.dateFrom && this.reportFilters.dateTo)
    },

    allSelected() {
      return (
        this.items.length > 0 &&
        this.items.every((it) => this.selectedIds.includes(it.id))
      )
    },

    someSelected() {
      return this.selectedIds.length > 0 && !this.allSelected
    },
  },

  watch: {
    showArchived() {
      this.selectedIds = []
      this.pagination.page = 1
      this.fetchItems()
    },
  },

  mounted() {
    this.fetchItems()
    if (!this.$store.state.project.projects.length) {
      this.$store
        .dispatch('project/getProjects', { limit: 100, is_archived: false })
        .catch(function () {})
    }
  },

  methods: {
    ...mapActions('plantEquipment', [
      'getPlantEquipment',
      'archivePlantEquipment',
      'restorePlantEquipment',
      'forceDeletePlantEquipment',
      'bulkDeletePlantEquipment',
      'getPlantCostReport',
      'exportPlantCostReport',
    ]),

    async fetchItems() {
      this.loading = true
      try {
        const params = {
          is_archived: this.showArchived ? 1 : 0,
          limit: this.pagination.perPage,
          page: this.pagination.page,
        }
        if (this.catalogueFilters.projectId != null) {
          params.project_id = this.catalogueFilters.projectId
        }
        if (this.catalogueFilters.search) {
          params.search = this.catalogueFilters.search
        }
        await this.getPlantEquipment(params)

        // Deleting the last item(s) on a page can leave pagination.page
        // pointing past the new last_page — the server doesn't clamp, it
        // just returns an empty page with nowhere to click back from (the
        // pagination bar itself is hidden when there are 0 items). Step
        // back once and refetch rather than strand the user there.
        if (
          this.meta &&
          this.pagination.page > 1 &&
          this.pagination.page > this.meta.last_page
        ) {
          this.pagination.page = this.meta.last_page
          await this.fetchItems()
          return
        }
      } catch (err) {
        this.$bvToast.toast('Failed to load equipment. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    onCatalogueFilterChange() {
      this.selectedIds = []
      this.pagination.page = 1
      this.fetchItems()
    },

    onPerPageChange() {
      this.selectedIds = []
      this.pagination.page = 1
      this.fetchItems()
    },

    goToPage(page) {
      if (page < 1 || (this.meta && page > this.meta.last_page)) return
      this.selectedIds = []
      this.pagination.page = page
      this.fetchItems()
    },

    formatRate(rate) {
      const n = Number(rate)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },

    formatReportDate(usageDay) {
      return usageDay ? dayjs(usageDay).format('DD/MM/YYYY') : '-'
    },

    formatReportDay(usageDay) {
      return usageDay ? dayjs(usageDay).format('dddd') : '-'
    },

    openCreateModal() {
      this.modalItem = null
      this.modalOpen = true
    },

    openEditModal(equip) {
      this.modalItem = equip
      this.modalOpen = true
    },

    onModalSaved() {
      this.fetchItems()
    },

    async onArchiveClick(equip) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Archive "${equip.name}"? Tradies won't be able to log new hours against it.`,
        {
          title: 'Archive equipment',
          okTitle: 'Archive',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      try {
        await this.archivePlantEquipment(equip.id)
        this.$bvToast.toast('Equipment archived.', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000,
        })
        if (!this.showArchived) this.fetchItems()
      } catch (err) {
        this.$bvToast.toast('Failed to archive equipment. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async onUnarchiveClick(equip) {
      try {
        await this.restorePlantEquipment(equip.id)
        this.$bvToast.toast('Equipment unarchived.', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000,
        })
        if (this.showArchived) this.fetchItems()
      } catch (err) {
        this.$bvToast.toast(
          'Failed to unarchive equipment. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
          },
        )
      }
    },

    toggleSelected(id, checked) {
      if (checked) {
        if (!this.selectedIds.includes(id)) this.selectedIds.push(id)
      } else {
        this.selectedIds = this.selectedIds.filter((i) => i !== id)
      }
    },

    toggleSelectAll(checked) {
      this.selectedIds = checked ? this.items.map((it) => it.id) : []
    },

    async onDeleteClick(equip) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Permanently delete "${equip.name}"? This cannot be undone.`,
        {
          title: 'Delete equipment',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      try {
        await this.forceDeletePlantEquipment(equip.id)
        this.selectedIds = this.selectedIds.filter((id) => id !== equip.id)
        this.$bvToast.toast('Equipment deleted.', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000,
        })
        // Refetch rather than trust the local splice — with pagination
        // active, removing an item can shift meta.total/last_page in ways
        // only the server knows (e.g. the last item on a page).
        await this.fetchItems()
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          'Failed to delete equipment. Please try again.'
        this.$bvToast.toast(message, {
          title:
            err.response && err.response.status === 409
              ? "Can't delete"
              : 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async onBulkDeleteClick() {
      const count = this.selectedIds.length
      if (!count) return

      const ok = await this.$bvModal.msgBoxConfirm(
        `Permanently delete ${count} selected item${
          count === 1 ? '' : 's'
        }? This cannot be undone.`,
        {
          title: 'Delete equipment',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!ok) return

      this.bulkDeleting = true
      try {
        const result = await this.bulkDeletePlantEquipment(this.selectedIds)
        const deletedCount = result.deleted_count || 0
        const failureCount = result.failure_count || 0

        if (failureCount === 0) {
          this.$bvToast.toast(`${deletedCount} item(s) deleted.`, {
            title: 'Success',
            variant: 'success',
            autoHideDelay: 3000,
          })
        } else {
          const reasons = (result.errors || []).map((e) => e.message).join(' ')
          this.$bvToast.toast(
            `${deletedCount} deleted. ${failureCount} skipped: ${reasons}`,
            {
              title: 'Some items were skipped',
              variant: 'warning',
              solid: true,
            },
          )
        }
        this.selectedIds = []
        await this.fetchItems()
      } catch (err) {
        this.$bvToast.toast(
          'Failed to delete selected equipment. Please try again.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
          },
        )
      } finally {
        this.bulkDeleting = false
      }
    },

    // Static template — generated client-side rather than via an API call,
    // since the content never depends on this BA's data (just the column
    // headers the import endpoint expects, per CsvPlantEquipmentImport on
    // the API side: name, plant_id_rego, rate_type, default_rate). Project
    // scope is not a column — it's chosen in the import modal.
    downloadCsvTemplate() {
      const rows = [
        ['name', 'plant_id_rego', 'rate_type', 'default_rate'],
        ['Excavator 5T', 'EXC-001', 'hourly', '120.50'],
        ['Bobcat 320', 'BOB-320-A', 'hourly', '85.00'],
        ['Concrete Mixer', '', 'day', '150.00'],
      ]
      const csvContent = rows.map((r) => r.join(',')).join('\r\n')
      // BOM so Excel doesn't guess Windows-1252 and mangle non-ASCII
      // equipment names a real BA might type in.
      const blob = new Blob(['\ufeff' + csvContent], {
        type: 'text/csv;charset=utf-8;',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'plant-equipment-template.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    async onCsvImported(result) {
      this.csvResult = {
        success_count: (result && result.success_count) || 0,
        failure_count: (result && result.failure_count) || 0,
        errors: (result && result.errors) || [],
      }
      this.csvResultOpen = true
      await this.fetchItems()
    },

    buildReportParams() {
      const params = {
        date_from: this.reportFilters.dateFrom,
        date_to: this.reportFilters.dateTo,
      }
      if (this.reportFilters.projectId != null)
        params.project_id = this.reportFilters.projectId
      return params
    },

    async runCostReport() {
      if (!this.canRunReport) return
      this.reportLoading = true
      try {
        const data = await this.getPlantCostReport(this.buildReportParams())
        this.reportRows = (data && data.rows) || []
        // Use the server's own total rather than re-summing rows client-side
        // — this is a billing-facing figure, and the two must never be able
        // to silently disagree (e.g. over rounding, or a future paginated
        // rows array).
        this.reportGrandTotal = (data && data.grand_total_claim_value) || 0
        this.reportRan = true
      } catch (err) {
        this.$bvToast.toast('Failed to load report. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.reportLoading = false
      }
    },

    async exportCostReport() {
      try {
        const blob = await this.exportPlantCostReport(this.buildReportParams())
        const filename = `plant-cost-detail-${this.reportFilters.dateFrom}-to-${this.reportFilters.dateTo}.csv`
        const csvBlob = new Blob([blob], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(csvBlob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.$bvToast.toast('Failed to export report. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.plant-toolbar-heading {
  min-width: 220px;
}

.plant-toolbar .timesheet-toolbar-top {
  margin-bottom: 0;
}

.plant-toolbar .timesheet-toolbar-actions {
  justify-content: flex-end;
}

.plant-archive-switch {
  flex: 0 0 auto;
  min-height: 22px;
  padding-left: 46px;
  margin-right: 4px;
  white-space: nowrap;
}

.plant-archive-switch ::v-deep .custom-control-label {
  min-height: 22px;
  padding-top: 2px;
  padding-left: 0;
  line-height: 18px;
}

.plant-archive-switch ::v-deep .custom-control-label::before {
  top: 0;
  left: -46px;
  width: 40px !important;
  height: 22px !important;
  border-color: #94a3b8;
  background: #94a3b8;
}

.plant-archive-switch ::v-deep .custom-control-label::after {
  top: 3px;
  left: -43px;
  width: 16px !important;
  height: 16px !important;
}

.plant-archive-switch
  ::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::before {
  border-color: #006cff;
  background: #006cff;
}

.plant-archive-switch
  ::v-deep
  .custom-control-input:checked
  ~ .custom-control-label::after {
  left: -25px;
  transform: none;
}

.plant-catalogue-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.plant-catalogue-filter-field {
  flex: 1 1 150px;
  max-width: 220px;
}

.plant-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 4px 4px;
}

.plant-pagination-size {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
}

.plant-pagination-size ::v-deep select {
  width: auto;
  height: 32px;
}

.plant-pagination-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.plant-pagination-label {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

.plant-bulk-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
}

.plant-bulk-count {
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
}

.plant-select-col {
  width: 36px;
}

.plant-table-wrap {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
}

.plant-table {
  margin-bottom: 0;

  thead th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    border-top: none;
    border-bottom: 1px solid #e5e7eb;
    background: #fff;
    padding: 10px 14px;
  }

  tbody td {
    padding: 12px 14px;
    vertical-align: middle;
    border-color: #f3f4f6;
    font-size: 13px;
  }

  tbody tr:hover {
    background: #f9fafb;
  }
}

.scope-badge {
  display: inline-block;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
  white-space: nowrap;
}

.scope-badge-all {
  background: #eff6ff;
  color: #1d4ed8;
}

.scope-badge-some {
  background: #f3f4f6;
  color: #374151;
  cursor: default;
}

.scope-popover-row {
  font-size: 12px;
  padding: 2px 0;
}

.plant-subnav {
  display: flex;
  gap: 8px;
  padding: 14px 20px 0;
  margin-bottom: 0;
}

.plant-subnav-item {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #006cff;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 18px;
  border-radius: 20px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;

  &:hover {
    border-color: #006cff;
    background: #dbeafe;
    color: #0056cc;
  }

  &.active {
    border-color: #006cff;
    background: #006cff;
    color: #fff;
  }
}

.cost-report-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.cost-report-field {
  min-width: 0;
}

.cost-report-project {
  flex: 1 1 150px;
  max-width: 220px;
}

.cost-report-date {
  flex: 1 1 190px;
  max-width: 240px;
}

.cost-report-control,
.cost-report-action {
  height: 40px;
}

.cost-report-action {
  min-width: 96px;
  padding-top: 0;
  padding-bottom: 0;
}

.cost-report-filters ::v-deep .cost-report-datepicker.form-control {
  display: flex;
  align-items: stretch;
  height: 40px;
  min-height: 40px;
  padding: 0;
}

.cost-report-datepicker ::v-deep > .btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  min-width: 42px;
  padding: 0;
}

.cost-report-datepicker ::v-deep > .form-control {
  display: flex;
  align-items: center;
  min-width: 0;
  height: auto;
  min-height: 0;
  padding: 0 10px;
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  /* Safety net if a locale/format ever produces a string wider than the
     field (the short date-format-options above is the actual fix) — clip
     instead of spilling text outside the box border. */
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1199.98px) {
  .plant-toolbar .timesheet-toolbar-top {
    flex-direction: column;
  }

  .plant-toolbar .timesheet-toolbar-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 575.98px) {
  .plant-toolbar .timesheet-toolbar-actions {
    align-items: stretch;
  }

  .plant-toolbar .timesheet-toolbar-actions .btn {
    flex: 1 1 calc(50% - 4px);
  }

  .plant-archive-switch {
    flex-basis: 100%;
    margin-bottom: 4px;
  }

  .plant-subnav {
    padding-right: 16px;
    padding-left: 16px;
  }

  .plant-subnav-item {
    flex: 1;
  }

  .cost-report-project,
  .cost-report-date {
    max-width: none;
  }

  .cost-report-field {
    flex-basis: 100%;
  }

  .cost-report-action {
    flex: 1 1 calc(50% - 6px);
  }
}
</style>
